import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';

// ─── FAQ data ─────────────────────────────────────────────────────────────────
const FAQS = [
  { q: 'What is DS codeXAI?', a: 'An AI-powered platform to create, modify, preview, and deploy apps using natural language.' },
  { q: 'Do I need coding knowledge?', a: 'No. Describe what you want and the AI builds it.' },
  { q: 'Can I modify my generated application?', a: 'Yes. Ask the AI Code Agent or edit the code directly.' },
  { q: 'Can I preview my application?', a: 'Yes. Use the Preview tab.' },
  { q: 'Can I deploy my application?', a: 'Yes. Use the Deploy button when ready.' },
];

// ─── Help-button tours ────────────────────────────────────────────────────────
const CREATE_PROJECT_TOUR = [
  { id: 'scratch', selector: '.pl-action-btn', title: 'Create a project', body: 'Click "Start from Scratch" to begin.', placement: 'bottom' },
  { id: 'type',    selector: '[data-tour="project-type"]', title: 'Choose project type', body: 'Pick Web App or Mobile App.', placement: 'bottom' },
  { id: 'prompt',  selector: '[data-tour="prompt-input"]', title: 'Describe your app', body: 'Tell the AI what to build.', example: '"Build a todo app with login and dashboard."', placement: 'top' },
];

const DEPLOY_TOUR = [
  { id: 'preview', selector: '[data-tour="tab-preview"]', title: 'Check your app', body: 'Verify it works in the Preview tab.', placement: 'bottom' },
  { id: 'deploy',  selector: '[data-tour="deploy-btn"]',  title: 'Deploy', body: 'Click Deploy when ready.', placement: 'bottom' },
];

// ─── Geometry helpers ─────────────────────────────────────────────────────────
function queryEl(selector) {
  if (!selector) return null;
  for (const s of selector.split(',').map(x => x.trim())) {
    try { const el = document.querySelector(s); if (el) return el; } catch {}
  }
  return null;
}

function getRect(selector) {
  const el = queryEl(selector);
  if (!el) return null;
  el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  return el.getBoundingClientRect();
}

function tooltipPos(rect, placement, tw, th) {
  if (!rect) return { top: '50%', left: '50%', transform: 'translate(-50%,-50%)' };
  const gap = 14, vw = window.innerWidth, vh = window.innerHeight;
  let top, left;
  if (placement === 'bottom') { top = rect.bottom + gap; left = rect.left + rect.width / 2 - tw / 2; }
  else if (placement === 'top') { top = rect.top - th - gap; left = rect.left + rect.width / 2 - tw / 2; }
  else if (placement === 'left') { top = rect.top + rect.height / 2 - th / 2; left = rect.left - tw - gap; }
  else { top = rect.top + rect.height / 2 - th / 2; left = rect.right + gap; }
  return { top: Math.max(12, Math.min(top, vh - th - 12)), left: Math.max(12, Math.min(left, vw - tw - 12)) };
}

// ─── Spotlight overlay ────────────────────────────────────────────────────────
function Spotlight({ rect, zIndex = 9990 }) {
  const PAD = 10;
  const spot = rect ? { x: rect.left - PAD, y: rect.top - PAD, w: rect.width + PAD * 2, h: rect.height + PAD * 2 } : null;
  return createPortal(
    <>
      {/* SVG dark overlay with cutout — pointerEvents none, clicks fall through */}
      <svg style={{ position: 'fixed', inset: 0, width: '100vw', height: '100vh', zIndex, pointerEvents: 'none' }} aria-hidden>
        <defs>
          <mask id="tour-mask">
            <rect width="100%" height="100%" fill="white" />
            {spot && <rect x={spot.x} y={spot.y} width={spot.w} height={spot.h} rx="10" fill="black" />}
          </mask>
        </defs>
        <rect width="100%" height="100%" fill="rgba(0,0,0,0.55)" mask="url(#tour-mask)" />
        {spot && (
          <>
            <rect x={spot.x} y={spot.y} width={spot.w} height={spot.h} rx="10"
              fill="none" stroke="#6366f1" strokeWidth="2.5" />
            <rect x={spot.x - 5} y={spot.y - 5} width={spot.w + 10} height={spot.h + 10} rx="14"
              fill="none" stroke="rgba(99,102,241,0.45)" strokeWidth="2">
              <animate attributeName="opacity" values="1;0.1;1" dur="1.4s" repeatCount="indefinite" />
            </rect>
          </>
        )}
      </svg>
      {/* Click-blocker: 4 divs around the spotlight hole so the highlighted element stays clickable */}
      {spot && (
        <>
          {/* top */}
          <div aria-hidden style={{ position: 'fixed', top: 0, left: 0, right: 0, height: spot.y, zIndex: zIndex + 1, background: 'transparent' }} />
          {/* bottom */}
          <div aria-hidden style={{ position: 'fixed', top: spot.y + spot.h, left: 0, right: 0, bottom: 0, zIndex: zIndex + 1, background: 'transparent' }} />
          {/* left */}
          <div aria-hidden style={{ position: 'fixed', top: spot.y, left: 0, width: spot.x, height: spot.h, zIndex: zIndex + 1, background: 'transparent' }} />
          {/* right */}
          <div aria-hidden style={{ position: 'fixed', top: spot.y, left: spot.x + spot.w, right: 0, height: spot.h, zIndex: zIndex + 1, background: 'transparent' }} />
        </>
      )}
    </>,
    document.body
  );
}

// ─── Tooltip card ─────────────────────────────────────────────────────────────
function Tooltip({ step, stepNum, totalSteps, rect, onSkip, onFinish, isLast, onNext, showNext }) {
  const ref = useRef(null);
  const [pos, setPos] = useState({ top: -9999, left: -9999 });

  const reposition = useCallback(() => {
    if (!ref.current) return;
    const { offsetWidth: w, offsetHeight: h } = ref.current;
    setPos(tooltipPos(rect, step.placement, w, h));
  }, [rect, step.placement]);

  useLayoutEffect(() => {
    reposition();
    const t = setTimeout(reposition, 120);
    window.addEventListener('resize', reposition);
    return () => { clearTimeout(t); window.removeEventListener('resize', reposition); };
  }, [reposition]);

  return createPortal(
    <div ref={ref} style={{ ...css.tooltip, top: pos.top, left: pos.left }} role="dialog" aria-modal="true">
      <div style={css.ttHeader}>
        <span style={css.stepBadge}>{stepNum} of {totalSteps}</span>
        <button style={css.skipLink} onClick={onSkip}>Skip tour</button>
      </div>
      <h3 style={css.ttTitle}>{step.title}</h3>
      <p style={css.ttBody}>{step.body}</p>
      {step.bullets && <ul style={css.list}>{step.bullets.map(b => <li key={b} style={css.listItem}>• {b}</li>)}</ul>}
      {step.example && <div style={css.exBox}><span style={css.exText}>{step.example}</span></div>}
      {showNext && (
        <button style={{ ...css.primaryBtn, marginTop: 4 }} onClick={onNext}>Next →</button>
      )}
      {isLast && (
        <button style={{ ...css.primaryBtn, marginTop: 4 }} onClick={onFinish}>Finish Tutorial 🎉</button>
      )}
    </div>,
    document.body
  );
}

// ─── Click-here arrow badge ───────────────────────────────────────────────────
function ClickArrow({ rect }) {
  if (!rect) return null;
  const x = rect.left + rect.width / 2;
  const y = rect.bottom + 14;
  return createPortal(
    <div style={{ position: 'fixed', top: y, left: x, transform: 'translateX(-50%)', zIndex: 10001, pointerEvents: 'none', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
      <div style={{ fontSize: 22, animation: 'tourBounce 0.9s ease-in-out infinite' }}>👆</div>
      <span style={{ fontSize: 11, fontWeight: 700, color: '#fff', background: '#6366f1', padding: '3px 10px', borderRadius: 20, whiteSpace: 'nowrap', boxShadow: '0 2px 8px rgba(99,102,241,0.5)' }}>
        Click here
      </span>
    </div>,
    document.body
  );
}

// ─── TourController — lives at AppShell level, persists across view changes ───
export function TourController({ view, newProjectMode, activeProjectId, onFinish, onSkip, step, onStep }) {
  const [rect, setRect] = useState(null);
  const tickRef = useRef(null);

  const SELECTORS = [
    '.pl-action-btn',
    '[data-tour="project-type"]',
    '[data-tour="project-name-input"]',
    '[data-tour="create-workspace-btn"]',
    '[data-tour="prompt-input"]',
  ];

  const STEPS = [
    { title: 'Step 1 of 5 — Create a project',   body: 'Click "Start from Scratch" to begin building your first app.', placement: 'bottom' },
    { title: 'Step 2 of 5 — Choose project type', body: 'Select Web App or Mobile App.', placement: 'bottom' },
    { title: 'Step 3 of 5 — Name your project',   body: 'Type a name for your project, then click Next.', placement: 'bottom' },
    { title: 'Step 4 of 5 — Open workspace',      body: 'Click "Create & Open Workspace" to continue.', placement: 'top' },
    { title: 'Step 5 of 5 — Describe your app',   body: 'Type what you want to build and press Send. The AI will generate it for you.', example: '"Build a todo app with login, task list, and dashboard."', placement: 'top' },
  ];

  // step 0→1: user clicked scratch btn, newProjectMode flipped
  useEffect(() => {
    if (step === 0 && newProjectMode) onStep(1);
  }, [newProjectMode, step]);

  // step 1→2: user clicked a project-type button
  useEffect(() => {
    if (step !== 1) return;
    const el = queryEl('[data-tour="project-type"]');
    if (!el) return;
    const handler = () => setTimeout(() => onStep(2), 150);
    el.addEventListener('click', handler);
    return () => el.removeEventListener('click', handler);
  }, [step]);

  // step 3→4: workspace opened after user clicked Create
  useEffect(() => {
    if (step === 3 && view === 'workspace' && activeProjectId) onStep(4);
  }, [view, activeProjectId, step]);

  // Poll rect — retry until element appears in DOM
  useEffect(() => {
    clearInterval(tickRef.current);
    const init = setTimeout(() => {
      const poll = () => setRect(getRect(SELECTORS[step]));
      poll();
      tickRef.current = setInterval(poll, 250);
    }, 80);
    return () => { clearTimeout(init); clearInterval(tickRef.current); };
  }, [step]);

  const currentStep = STEPS[step];
  const isLast = step === 4;
  const showArrow = (step === 0 || step === 3) && !!rect;
  const showNext  = step === 2;

  return (
    <>
      <style>{`@keyframes tourBounce{0%,100%{transform:translateY(0)}50%{transform:translateY(-7px)}}`}</style>
      <Spotlight rect={rect} zIndex={9990} />
      {showArrow && <ClickArrow rect={rect} />}
      <Tooltip
        step={currentStep}
        stepNum={step + 1}
        totalSteps={5}
        rect={rect}
        onSkip={onSkip}
        onFinish={onFinish}
        isLast={isLast}
        showNext={showNext}
        onNext={() => onStep(3)}
      />
    </>
  );
}

// ─── Welcome Modal ────────────────────────────────────────────────────────────
export function WelcomeModal({ onStart, onSkip }) {
  return createPortal(
    <div style={css.overlay} role="dialog" aria-modal="true">
      <div style={css.card}>
        <div style={{ fontSize: 36, marginBottom: 4 }}>👋</div>
        <h2 style={css.cardTitle}>Welcome to DS codeXAI</h2>
        <p style={css.cardSub}>Let's build your first app together. I'll guide you through 5 quick steps.</p>
        <ul style={css.featureList}>
          {[
            '① Click "Start from Scratch"',
            '② Choose Web or Mobile app',
            '③ Enter a project name',
            '④ Click "Create & Open Workspace"',
            '⑤ Describe your app to the AI',
          ].map(t => (
            <li key={t} style={css.featureItem}><span style={css.featureDot} /><span>{t}</span></li>
          ))}
        </ul>
        <div style={css.actions}>
          <button style={css.primaryBtn} onClick={onStart}>Start Tutorial →</button>
          <button style={css.ghostBtn} onClick={onSkip}>Skip</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── FAQs Modal ───────────────────────────────────────────────────────────────
export function FAQsModal({ onClose }) {
  return createPortal(
    <div style={css.overlay} onClick={onClose}>
      <div style={{ ...css.card, maxWidth: 520, maxHeight: '80vh', overflowY: 'auto', textAlign: 'left' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ ...css.cardTitle, margin: 0 }}>FAQs</h2>
          <button style={css.closeBtn} onClick={onClose}>✕</button>
        </div>
        {FAQS.map(({ q, a }) => (
          <div key={q} style={{ marginBottom: 16 }}>
            <p style={css.faqQ}>{q}</p>
            <p style={css.faqA}>{a}</p>
          </div>
        ))}
      </div>
    </div>,
    document.body
  );
}

// ─── GuidedTour (used by HelpButton) ─────────────────────────────────────────
export function GuidedTour({ steps, onFinish, onSkip, finishLabel = 'Done' }) {
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);
  const step = steps[idx];

  useEffect(() => {
    const r = getRect(step.selector);
    setRect(r);
    const t = setTimeout(() => setRect(getRect(step.selector)), 200);
    return () => clearTimeout(t);
  }, [step]);

  const isLast = idx === steps.length - 1;

  return (
    <>
      <Spotlight rect={rect} />
      <Tooltip
        step={step}
        stepNum={idx + 1}
        totalSteps={steps.length}
        rect={rect}
        onSkip={onSkip}
        onFinish={onFinish}
        isLast={isLast}
        showNext={!isLast}
        onNext={() => setIdx(i => i + 1)}
      />
    </>
  );
}

// ─── Help Button ──────────────────────────────────────────────────────────────
export function HelpButton({ onStartTour, onNavigateProjects, onGetView, defaultOpen = null }) {
  const [open, setOpen] = useState(false);
  const [activeTour, setActiveTour] = useState(defaultOpen);
  const [showFAQs, setShowFAQs] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  const startTour = (key) => {
    setOpen(false);
    if (['agent', 'deploy'].includes(key) && onGetView?.() !== 'workspace') onNavigateProjects?.();
    setActiveTour(key);
  };

  const ITEMS = [
    { label: 'Take Product Tour',        action: () => { setOpen(false); onStartTour?.(); } },
    { label: 'How to Create a Project',  action: () => startTour('create') },
    { label: 'How to Deploy',            action: () => startTour('deploy') },
    { label: 'FAQs',                     action: () => { setOpen(false); setShowFAQs(true); } },
  ];

  const tourMap = { create: CREATE_PROJECT_TOUR, deploy: DEPLOY_TOUR };

  return (
    <>
      <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
        <button style={css.helpBtn} onClick={() => setOpen(o => !o)} aria-label="Help" title="Help & Resources">?</button>
        {open && (
          <div style={css.helpMenu}>
            <div style={css.helpMenuTitle}>Help &amp; Resources</div>
            {ITEMS.map(item => (
              <button key={item.label} style={css.helpMenuItem} onClick={item.action}>{item.label}</button>
            ))}
          </div>
        )}
      </div>
      {activeTour && tourMap[activeTour] && (
        <GuidedTour steps={tourMap[activeTour]} onFinish={() => setActiveTour(null)} onSkip={() => setActiveTour(null)} />
      )}
      {showFAQs && <FAQsModal onClose={() => setShowFAQs(false)} />}
    </>
  );
}

// ─── Main Onboarding — Welcome modal only. TourController is lifted to AppShell.
export default function Onboarding({ status, onSave, onStartTour, onSkip }) {
  const [phase, setPhase] = useState(null);

  useEffect(() => {
    // Only show welcome modal for brand-new users
    if (status === 'not_started') setPhase('welcome');
    else setPhase(null);
  }, [status]);

  if (phase !== 'welcome') return null;

  return (
    <WelcomeModal
      onStart={() => { onSave('in_progress'); setPhase(null); onStartTour(); }}
      onSkip={() => { onSave('skipped'); setPhase(null); onSkip?.(); }}
    />
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const css = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' },
  card: { background: 'var(--bg-card,#1e293b)', border: '1px solid rgba(99,102,241,0.25)', borderRadius: 16, padding: '32px 28px 24px', maxWidth: 420, width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.5)', display: 'flex', flexDirection: 'column', gap: 14, textAlign: 'center' },
  cardTitle: { fontSize: 20, fontWeight: 700, color: 'var(--text-primary,#f1f5f9)', margin: 0, letterSpacing: '-0.02em' },
  cardSub: { fontSize: 13, color: 'var(--text-secondary,#94a3b8)', margin: 0, lineHeight: 1.6 },
  featureList: { listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 7, textAlign: 'left' },
  featureItem: { display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: 'var(--text-primary,#f1f5f9)', background: 'rgba(99,102,241,0.07)', border: '1px solid rgba(99,102,241,0.12)', borderRadius: 8, padding: '8px 12px' },
  featureDot: { width: 6, height: 6, borderRadius: '50%', background: '#6366f1', flexShrink: 0 },
  actions: { display: 'flex', gap: 10, width: '100%', marginTop: 4 },

  tooltip: { position: 'fixed', zIndex: 10001, width: 300, background: 'var(--bg-card,#1e293b)', border: '1px solid rgba(99,102,241,0.35)', borderRadius: 12, padding: '16px 18px 14px', boxShadow: '0 12px 40px rgba(0,0,0,0.5)', display: 'flex', flexDirection: 'column', gap: 9 },
  ttHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  stepBadge: { fontSize: 11, fontWeight: 700, color: '#818cf8', background: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.2)', padding: '2px 9px', borderRadius: 20 },
  skipLink: { background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: 'var(--text-muted,#64748b)', padding: '2px 4px' },
  ttTitle: { fontSize: 14, fontWeight: 700, color: 'var(--text-primary,#f1f5f9)', margin: 0 },
  ttBody: { fontSize: 13, color: 'var(--text-secondary,#94a3b8)', margin: 0, lineHeight: 1.6 },
  ttFooter: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 2 },
  list: { listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 3 },
  listItem: { fontSize: 12, color: '#a5b4fc' },
  exBox: { background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.16)', borderRadius: 7, padding: '7px 11px' },
  exText: { fontSize: 12, color: '#a5b4fc', fontStyle: 'italic' },

  primaryBtn: { flex: 1, padding: '10px 18px', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer' },
  ghostBtn: { padding: '10px 14px', background: 'transparent', color: 'var(--text-secondary,#94a3b8)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer' },
  closeBtn: { background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted,#64748b)', fontSize: 16, padding: '4px 6px' },

  helpBtn: { width: 32, height: 32, borderRadius: '50%', background: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.3)', color: '#818cf8', fontSize: 14, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  helpMenu: { position: 'absolute', top: 38, right: 0, background: 'var(--bg-card,#1e293b)', border: '1px solid rgba(99,102,241,0.2)', borderRadius: 10, boxShadow: '0 10px 36px rgba(0,0,0,0.4)', minWidth: 210, zIndex: 9999, overflow: 'hidden', display: 'flex', flexDirection: 'column' },
  helpMenuTitle: { fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: 'var(--text-muted,#64748b)', textTransform: 'uppercase', padding: '10px 14px 6px' },
  helpMenuItem: { display: 'block', width: '100%', padding: '9px 14px', background: 'none', border: 'none', color: 'var(--text-primary,#f1f5f9)', fontSize: 13, fontWeight: 500, cursor: 'pointer', textAlign: 'left' },

  faqQ: { fontSize: 14, fontWeight: 600, color: 'var(--text-primary,#f1f5f9)', margin: '0 0 4px' },
  faqA: { fontSize: 13, color: 'var(--text-secondary,#94a3b8)', margin: 0, lineHeight: 1.6 },
};
